import { ErrorDialogContext, errorHandledAsync } from "@/shared/context/error-context";
import { useContext } from "react";
import { LoadingModalContext } from "@/shared/components/loading/loading-context";
import { TwoStepAuthService } from "@/features/two-step-authentication/services/two-step-authentication-service";
import { MoneyLinkException } from "@/shared/utils/custom-exception";
import { useLoginContext } from "@/features/auth/contexts/login-context";


interface CodeProps {
  onCompleteModal: (result: CodeModalResult) => void;
}

export type CodeModalResult = {
  status: "" | "ok" | "failed" | "cancel";
};

// 2段階認証設定画面用フック
export const useTwoStepAuthCode = ({ onCompleteModal }: CodeProps) => {
  const twoStepAuthService = new TwoStepAuthService();
  const {
    twoStepAuthCode,
    twoStepReleaseCode,
    twoStepSetCode,
  } = useLoginContext();

  // グローバルエラーハンドリング
  const { setError } = useContext(ErrorDialogContext);
  // ローディング用コンテキスト
  const loadingCtx = useContext(LoadingModalContext);

  // 2段階認証実行ボタン押下イベント
  const verifyCode = async () => {
    loadingCtx.setIsLoading(true);
    try {
      // 2段階認証コード検証API実行
      await twoStepAuthService.verifyTwoStepAuthCode({
        authentication_code: twoStepAuthCode,
        purpose: "login",
      });

      onCompleteModal({ status: "ok" });
    } catch (error) {
      console.log(error);
      // APIエラーは再throw してerrorHandledAsyncで処理
      throw error;
    } finally {
      loadingCtx.setIsLoading(false);
    }
  };

  // 2段階認証解除ボタン押下イベント
  const verifyReleaseCode = async () => {
    loadingCtx.setIsLoading(true);
    try {
      // 2段階認証解除コード検証API実行
      await twoStepAuthService.verifyTwoStepAuthReleaseCode({
        release_code: twoStepReleaseCode,
      });

      // 2段階認証を解除しましたのメッセージを出す必要アリ
      onCompleteModal({ status: "ok" });
    } catch (error) {
      throw MoneyLinkException.wrapCallback(error, () => { /**intentionally empty */ });
    } finally {
      loadingCtx.setIsLoading(false);
    }
  };

  // 2段階認証設定登録ボタン押下イベント
  const verifySetCode = async () => {
    loadingCtx.setIsLoading(true);
    try {
      // 2段階認証コード検証API実行
      await twoStepAuthService.verifyTwoStepAuthCode({
        authentication_code: twoStepSetCode,
        purpose: "setting",
      });

      await twoStepAuthService.updateTwoStepAuth({
        is_used: 1, // 1:有効
      });
      onCompleteModal({ status: "ok" });
    } catch (error) {
      throw MoneyLinkException.wrapCallback(error, () => { /**intentionally empty */ });
    } finally {
      loadingCtx.setIsLoading(false);
    }
  };

  return {
    verifyCode: errorHandledAsync(verifyCode, setError),
    verifyReleaseCode: errorHandledAsync(verifyReleaseCode, setError),
    verifySetCode: errorHandledAsync(verifySetCode, setError),
  };
};
